import { Button, Callout, Card, Divider, FormGroup, NumericInput, Switch } from '@blueprintjs/core'

import { WeldingParams } from '../components/WeldingParams'
import { ToolPanel } from './ToolPanel'
import type { Operation, Store } from '../store'

/**
 * Left-swap panel that shows context-dependent forms.
 *
 * Mirrors SolidWorks PropertyManager:
 *  - top bar with ✔ (Enter) and ✖ (Esc)
 *  - a stack of FormGroup rows
 *  - selection pickers that activate a viewport-pick mode
 *
 * Phase 1 wires the shell; Phase 3 fills out per-operation forms, Phase 7
 * fills out the Tool import form.
 */
export function PropertyManagerPanel({ store }: { store: Store }) {
  const sel = store.selection
  if (!sel) {
    return (
      <div className="pm-empty">
        <Callout icon="info-sign" intent="none">
          Select an item in the tree, or click a ribbon command to create one.
        </Callout>
      </div>
    )
  }

  if (sel.kind === 'tool') {
    return <ToolPanel store={store} />
  }

  if (sel.kind === 'operation') {
    const op = store.job.operations.find((o) => o.id === sel.id)
    if (!op) return null
    return <OperationPanel op={op} store={store} />
  }

  return (
    <div className="pm-static">
      <Card>
        <h3>{sel.kind}</h3>
        <p>Property editor for &quot;{sel.kind}&quot; is coming in a later phase.</p>
      </Card>
    </div>
  )
}

function OperationPanel({ op, store }: { op: Operation; store: Store }) {
  const commit = () => store.commitPendingOperation()
  const cancel = () => store.cancelPendingOperation()

  return (
    <div className="pm">
      <div className="pm-header">
        <Button icon="tick" intent="success" minimal onClick={commit} title="OK (Enter)" />
        <Button icon="cross" intent="danger" minimal onClick={cancel} title="Cancel (Esc)" />
        <span className="pm-title">
          {op.kind === 'clad' ? 'Clad' : 'Seam weld'}: {op.label}
        </span>
      </div>
      <Divider />
      <div className="pm-body">
        <FormGroup label="Label" labelFor={`op-${op.id}-label`}>
          <input
            id={`op-${op.id}-label`}
            className="bp5-input bp5-fill"
            value={op.label}
            onChange={(e) => store.updateOperation(op.id, { label: e.target.value })}
          />
        </FormGroup>
        <FormGroup label="Face selection" helperText="Click a face in the viewport.">
          <Button
            icon="selection"
            intent={store.pickMode === op.id ? 'primary' : 'none'}
            onClick={() => store.togglePickMode(op.id)}
            text={
              op.kind === 'clad'
                ? `${op.faces.length} face(s) selected`
                : `${op.seamPairs.length} seam pair(s) selected`
            }
          />
        </FormGroup>
        <FormGroup label="Layers">
          <NumericInput
            min={1}
            max={10}
            value={op.wps.layers}
            onValueChange={(v) =>
              store.updateOperation(op.id, { wps: { ...op.wps, layers: v || 1 } })
            }
          />
        </FormGroup>
        <Divider />
        <WeldingParams
          wps={op.wps}
          onChange={(wps) => store.updateOperation(op.id, { wps })}
        />
        <Divider />
        <FormGroup inline label="Emit arc">
          <Switch
            checked={op.wps.emit_arc}
            onChange={(e) =>
              store.updateOperation(op.id, {
                wps: { ...op.wps, emit_arc: (e.target as HTMLInputElement).checked },
              })
            }
          />
        </FormGroup>
      </div>
    </div>
  )
}
